import { ColumnHelper, HeaderContext } from "@tanstack/react-table";
import isEmpty from "lodash/isEmpty";
import { Typography } from "PFComponents/typography";
import { Color, UsersImportRow, UsersImportRowState } from "PFTypes";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { ImportStatePill } from "../import_modal/components/parts/import_state_pill";
import { ObjectDisplay } from "../import_modal/components/parts/object_display";

const STATE_COLORS: Record<UsersImportRowState, { background: Color; text: Color }> = {
  created: { background: "paletteGreen0", text: "paletteWhite0" },
  updated: { background: "paletteBlue0", text: "paletteWhite0" },
  invalid: { background: "paletteRed1", text: "paletteWhite0" },
  processing: { background: "paletteNeutral0", text: "paletteBlue0" },
  queued: { background: "paletteNeutral0", text: "paletteBlue0" }
};

export const useColumnsGenerator = () => {
  const { t } = useTranslation("profiles", { keyPrefix: "importProfiles.history" });

  const header = useCallback(
    (header: HeaderContext<UsersImportRow, unknown>) => (
      <Typography withMargin variant="bodyRegular" tag="span">
        {t(`headers.${header.header.id}` as unknown as TemplateStringsArray)}
      </Typography>
    ),
    [t]
  );

  return useCallback(
    (columnHelper: ColumnHelper<UsersImportRow>) => [
      columnHelper.accessor((row) => row.rowNumber, {
        id: "rowNumber",
        header,
        cell: (info) => info.getValue(),
        size: 75,
        enableSorting: false
      }),
      columnHelper.accessor((row) => row.userId, {
        id: "userId",
        header,
        cell: (info) => info.getValue(),
        size: 75,
        enableSorting: false
      }),
      columnHelper.accessor((row) => row.userEmail, {
        id: "email",
        header,
        cell: (info) => info.getValue(),
        size: 150,
        enableSorting: false
      }),
      columnHelper.accessor((row) => row.state, {
        id: "state",
        header,
        cell: (info) => {
          const state = info.getValue();
          const colors = STATE_COLORS[state];
          return state ? (
            <ImportStatePill
              text={t(`counters.${state}`)}
              backgroundColor={colors.background}
              textColor={colors.text}
            />
          ) : null;
        },
        size: 100,
        enableSorting: false
      }),
      columnHelper.accessor((row) => row.rowValue, {
        id: "rowValue",
        header,
        cell: (info) => !isEmpty(info.getValue()) && <ObjectDisplay object={info.getValue()} />,
        size: 150,
        enableSorting: false
      }),
      columnHelper.accessor((row) => row.rowErrors, {
        id: "errors",
        header,
        cell: (info) => !isEmpty(info.getValue()) && <ObjectDisplay object={info.getValue()} />,
        size: 150,
        enableSorting: false
      })
    ],
    []
  );
};
